import React from "react";
import { motion } from "framer-motion";
import { Landmark, Rocket, Handshake, Compass } from "lucide-react";
import { IMAGES, Reveal, Eyebrow, EASE } from "./shared";

const CARDS = [
  { icon: Landmark, title: "Brands", text: "Helping businesses establish meaningful market presence in DNHDD, and building the scalable foundations to grow with confidence." },
  { icon: Rocket, title: "Ventures", text: "Building businesses designed around regional opportunities and long-term commercial potential." },
  { icon: Handshake, title: "Partnerships", text: "Working alongside manufacturers, entrepreneurs and strategic partners to create enduring value." },
  { icon: Compass, title: "Advisory", text: "Guiding businesses through DNHDD's regulatory, commercial and cultural landscape — from first entry to long-term positioning." },
];

export const WhatWeBuild = () => {
  return (
    <section
      id="build"
      data-testid="build-section"
      className="relative pt-28 lg:pt-40"
      style={{ backgroundColor: "#F2F1EF" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>What We Build</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-bold leading-[1.08] text-3xl sm:text-4xl lg:text-5xl" style={{ color: "#2B2B2B" }}>
              Creating <span style={{ color: "#3B2C82" }}>Long-Term Value</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} as="p" className="mt-6 text-base lg:text-lg leading-relaxed" style={{ color: "#8C8C8C" }}>
            We create value in two ways — building ventures of our own, and standing alongside
            businesses that choose to build in DNHDD themselves.
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                data-testid={`build-card-${c.title.toLowerCase()}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.12 }}
                whileHover={{ y: -6 }}
                className="group p-8 rounded-sm flex flex-col min-h-[280px] transition-colors duration-300"
                style={{ backgroundColor: "#FAF9F7", border: "1px solid #D8D8D8" }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-sm mb-8 transition-colors duration-300"
                  style={{ border: "1px solid #D8D8D8", color: "#3B2C82" }}
                >
                  <Icon size={22} strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium" style={{ color: "#8C8C8C" }}>
                    0{i + 1}
                  </span>
                  <h3 className="text-xl font-bold" style={{ color: "#2B2B2B" }}>{c.title}</h3>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: "#8C8C8C" }}>{c.text}</p>
                <span
                  className="mt-auto pt-6 h-0.5 w-0 group-hover:w-10 transition-[width] duration-500"
                  style={{ background: "#F5821F" }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Full-width image band */}
      <Reveal className="mt-20 lg:mt-28 relative h-[42vh] lg:h-[56vh] overflow-hidden">
        <motion.img
          src={IMAGES.build}
          alt="Harbor at dusk with moored vessels"
          className="w-full h-full object-cover"
          initial={{ scale: 1.12 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: EASE }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(31,26,56,0.32)" }} />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 w-full pb-10">
            <p className="max-w-xl text-white text-lg lg:text-2xl font-medium leading-snug">
              Scalable foundations, built for markets we understand deeply.
            </p>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
